import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { booksApi, shelfApi, reviewsApi } from '@/lib/api';
import { getUserFriendlyError } from '@/lib/errorMessages';
import AppNavbar from '@/components/app/AppNavbar';
import StarRating from '@/components/app/StarRating';
import TimeAgo from '@/components/app/TimeAgo';
import DiscussionTab from '@/components/app/DiscussionTab';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { BookOpen, Plus, ThumbsUp, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import MobileBottomNav from '@/components/app/MobileBottomNav';
import SEOMeta from '@/components/app/SEOMeta';

const SHELF_STATES = [
  { value: 'read', label: 'Read' },
  { value: 'reading', label: 'Currently Reading' },
  { value: 'want', label: 'Want to Read' },
  { value: 'own', label: 'Own' },
  { value: 'dnf', label: 'Did Not Finish' },
];

const SHELF_COLORS = {
  read: 'bg-forest/10 text-forest border-forest/30',
  reading: 'bg-terra/10 text-terra border-terra/30',
  want: 'bg-gold/10 text-ink-light border-gold/30',
  own: 'bg-ink/10 text-ink border-ink/20',
  dnf: 'bg-red-100 text-red-700 border-red-200',
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function ReviewCard({ review, onMarkHelpful }) {
  const navigate = useNavigate();
  return (
    <div className="bg-cream border border-cream-dark rounded-xl p-4">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(`/profile/${review.user?.username}`)}>
          <Avatar className="w-8 h-8">
            <AvatarImage src={review.user?.avatar_url} />
            <AvatarFallback className="bg-terra text-cream text-xs">
              {getInitials(review.user?.display_name || review.user?.username)}
            </AvatarFallback>
          </Avatar>
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/profile/${review.user?.username}`)}
              className="text-sm font-semibold text-ink hover:text-terra transition-colors"
            >
              {review.user?.display_name || review.user?.username}
            </button>
            <TimeAgo date={review.created_at} className="text-xs" />
          </div>
          {review.star_rating > 0 && (
            <StarRating value={review.star_rating} readonly size="sm" />
          )}
          {review.body && (
            <p className="text-sm text-ink-light leading-relaxed mt-1">{review.body}</p>
          )}
          <button
            onClick={() => onMarkHelpful(review.id)}
            className="flex items-center gap-1 mt-2 text-xs text-ink-muted hover:text-terra transition-colors"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            <span>Helpful {review.helpful_count > 0 ? `(${review.helpful_count})` : ''}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BookPage() {
  const { bookId, googleBooksId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isGoogleId = !!googleBooksId;

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [myShelfEntry, setMyShelfEntry] = useState(null);

  const [shelfDialogOpen, setShelfDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const [reviewBody, setReviewBody] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selectedShelfStates, setSelectedShelfStates] = useState([]);

  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      try {
        const bookData = await booksApi.getBook(isGoogleId ? googleBooksId : bookId);
        setBook(bookData);

        if (user && bookData.id) {
          try {
            const shelf = await shelfApi.getMyShelf();
            const entry = (Array.isArray(shelf) ? shelf : shelf?.items || [])
              .find((item) => item.book?.id === bookData.id || item.book_id === bookData.id);
            if (entry) {
              setMyShelfEntry(entry);
              setSelectedShelfStates(entry.shelf_states || [entry.shelf_state] || []);
            }
          } catch (_) {}
        }

        const rev = await reviewsApi.getReviews(bookData.id || bookData.google_books_id);
        setReviews(Array.isArray(rev) ? rev : rev?.items || []);
      } catch (err) {
        toast.error('Could not load book');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookId, googleBooksId, isGoogleId, user]);

  const handleAddToShelf = async () => {
    if (!user) { navigate('/login'); return; }
    if (selectedShelfStates.length === 0) { toast.error('Please select at least one shelf'); return; }
    setSubmitting(true);
    try {
      if (myShelfEntry) {
        await shelfApi.updateBook(myShelfEntry.id, { shelf_states: selectedShelfStates });
        setMyShelfEntry({ ...myShelfEntry, shelf_states: selectedShelfStates });
        toast.success('Shelf updated!');
      } else {
        const entry = await shelfApi.addBook({
          google_books_id: book.google_books_id,
          shelf_states: selectedShelfStates,
        });
        setMyShelfEntry(entry);
        toast.success('Added to shelf!');
      }
      setShelfDialogOpen(false);
    } catch (err) {
      toast.error(getUserFriendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFromShelf = async () => {
    if (!myShelfEntry) return;
    setSubmitting(true);
    try {
      await shelfApi.removeBook(myShelfEntry.id);
      setMyShelfEntry(null);
      setSelectedShelfStates([]);
      toast.success('Removed from shelf');
      setShelfDialogOpen(false);
    } catch (err) {
      toast.error(getUserFriendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewBody.trim()) { toast.error('Please write a review'); return; }
    if (reviewRating === 0) { toast.error('Please select a star rating'); return; }
    setSubmitting(true);
    try {
      const review = await reviewsApi.createReview(book.id || book.google_books_id, {
        body: reviewBody.trim(),
        star_rating: reviewRating,
      });
      setReviews((prev) => [{ ...review, user }, ...prev]);
      setReviewBody('');
      setReviewRating(0);
      setReviewDialogOpen(false);
      toast.success('Review posted!');
    } catch (err) {
      toast.error(err.message || 'Failed to post review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    try {
      await reviewsApi.markHelpful(reviewId);
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
        )
      );
    } catch (_) {}
  };

  const avgRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + (r.star_rating || 0), 0) / reviews.length
      : 0;

  const canReview = user && (myShelfEntry?.shelf_states?.includes('read') || myShelfEntry?.shelf_states?.includes('reading'));

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <AppNavbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex gap-6 mb-8">
            <Skeleton className="w-32 h-48 rounded-lg bg-cream-dark flex-shrink-0" />
            <div className="space-y-3 flex-1">
              <Skeleton className="h-8 w-64 bg-cream-dark" />
              <Skeleton className="h-4 w-40 bg-cream-dark" />
              <Skeleton className="h-4 w-32 bg-cream-dark" />
              <Skeleton className="h-10 w-36 bg-cream-dark mt-4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-cream">
        <AppNavbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <BookOpen className="w-12 h-12 text-ink-muted mx-auto mb-4 opacity-50" />
          <h2 className="font-heading text-2xl text-ink mb-2">Book not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      {book && (
        <SEOMeta
          title={`${book.title}${book.author ? ` by ${book.author}` : ''}`}
          description={book.description || `${book.title} by ${book.author} — on ReadTheRoom`}
          image={book.cover_url}
          type="book"
        />
      )}
      <AppNavbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Book Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-cream border border-cream-dark rounded-2xl p-6 mb-6"
        >
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Cover */}
            <div className="w-28 sm:w-36 flex-shrink-0 mx-auto sm:mx-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-cream-dark shadow-md">
                {book.cover_url || book.thumbnail ? (
                  <img
                    src={book.cover_url || book.thumbnail}
                    alt={book.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-3">
                    <BookOpen className="w-8 h-8 text-ink-muted" />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="font-heading text-2xl sm:text-3xl text-ink leading-tight mb-1">
                {book.title}
              </h1>
              <p className="text-ink-muted text-base mb-3">
                by {book.author || book.authors}
              </p>

              {/* Community stats */}
              <div className="flex flex-wrap gap-3 mb-4">
                {book.read_count > 0 && (
                  <div className="text-center">
                    <span className="text-sm font-semibold text-ink">{book.read_count}</span>
                    <span className="text-xs text-ink-muted ml-1">read</span>
                  </div>
                )}
                {book.want_count > 0 && (
                  <div className="text-center">
                    <span className="text-sm font-semibold text-ink">{book.want_count}</span>
                    <span className="text-xs text-ink-muted ml-1">want to read</span>
                  </div>
                )}
                {book.own_count > 0 && (
                  <div className="text-center">
                    <span className="text-sm font-semibold text-ink">{book.own_count}</span>
                    <span className="text-xs text-ink-muted ml-1">own</span>
                  </div>
                )}
              </div>

              {/* Average rating */}
              {avgRating > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <StarRating value={Math.round(avgRating)} readonly size="sm" />
                  <span className="text-sm text-ink-muted">
                    {avgRating.toFixed(1)} ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                  </span>
                </div>
              )}

              {/* Shelf button */}
              <div className="flex items-center gap-2">
                {myShelfEntry ? (
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedShelfStates(myShelfEntry.shelf_states || []); setShelfDialogOpen(true); }}
                    className="border border-cream-dark text-ink"
                  >
                    {myShelfEntry.shelf_states?.length > 0 ? (
                      myShelfEntry.shelf_states.map(s => SHELF_STATES.find(st => st.value === s)?.label || s).join(', ')
                    ) : 'On Shelf'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => { setSelectedShelfStates(['want']); setShelfDialogOpen(true); }}
                    className="bg-terra hover:bg-terra/90 text-cream"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Shelf
                  </Button>
                )}
              </div>

              {/* Description */}
              {book.description && (
                <p className="text-sm text-ink-light mt-4 leading-relaxed line-clamp-4">
                  {book.description}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Reviews & Discussion Tabs */}
        <Tabs defaultValue="reviews">
          <div className="flex items-center justify-between mb-4">
            <TabsList className="bg-cream-dark">
              <TabsTrigger
                value="reviews"
                className="data-[state=active]:bg-terra data-[state=active]:text-cream"
              >
                Reviews ({reviews.length})
              </TabsTrigger>
              <TabsTrigger
                value="discussion"
                className="data-[state=active]:bg-terra data-[state=active]:text-cream"
              >
                Discussion
              </TabsTrigger>
            </TabsList>

            {user && canReview && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewDialogOpen(true)}
                className="border-cream-dark text-ink hover:border-terra hover:text-terra text-xs"
              >
                Write Review
              </Button>
            )}
          </div>

          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-ink-muted text-sm">No reviews yet. Be the first!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} onMarkHelpful={handleMarkHelpful} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="discussion">
            <DiscussionTab
              bookId={book?.id}
              user={user}
              myShelfState={myShelfEntry?.shelf_states?.[0]}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Shelf Dialog */}
      <Dialog open={shelfDialogOpen} onOpenChange={setShelfDialogOpen}>
        <DialogContent className="bg-cream border-cream-dark max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-ink">
              {myShelfEntry ? 'Update Shelf' : 'Add to Shelf'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-ink-muted mb-2">Select one or more categories:</p>
            {SHELF_STATES.map((state) => (
              <label
                key={state.value}
                className="flex items-center gap-3 px-4 py-3 rounded-lg border border-cream-dark hover:border-terra/40 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedShelfStates.includes(state.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedShelfStates([...selectedShelfStates, state.value]);
                    } else {
                      setSelectedShelfStates(selectedShelfStates.filter(s => s !== state.value));
                    }
                  }}
                  className="w-4 h-4 text-terra border-cream-dark rounded focus:ring-terra"
                />
                <span className="text-ink flex-1">{state.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            {myShelfEntry && (
              <Button
                variant="outline"
                onClick={handleRemoveFromShelf}
                disabled={submitting}
                className="border-red-200 text-red-500 hover:bg-red-50 text-sm flex-1"
              >
                Remove from Shelf
              </Button>
            )}
            <Button
              onClick={handleAddToShelf}
              disabled={submitting || selectedShelfStates.length === 0}
              className="bg-terra hover:bg-terra/90 text-cream flex-1"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Write Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-cream border-cream-dark max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-ink">Write a Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-ink-muted mb-2">Your rating</p>
              <StarRating value={reviewRating} onChange={setReviewRating} size="lg" />
            </div>
            <div>
              <Textarea
                placeholder="Share your thoughts about this book..."
                value={reviewBody}
                onChange={(e) => setReviewBody(e.target.value)}
                className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted min-h-[120px] resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setReviewDialogOpen(false)}
              className="text-ink-muted"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submitting}
              className="bg-terra hover:bg-terra/90 text-cream"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
}
